/**
 * LanguageSelect — sélecteur de langue (primitive UIX, i18n-multilang-engine Phase 1b).
 *
 * Trigger = drapeau uniquement (langue courante). Menu = drapeau + nom (usabilité/a11y :
 * un drapeau seul est ambigu). Sélection → i18next.changeLanguage (persisté localStorage
 * par le LanguageDetector) + synchro User.preferredLanguage si connecté (fire-and-forget).
 *
 * DA : tokens theme.js uniquement (§20). Aucune couleur en dur ici (drapeaux = assets SVG).
 */
import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import useOnclickOutside from 'react-cool-onclickoutside';
import {COLORS, RADII, SHADOW, FONT_SIZE} from './theme';
import * as selectors from '../../store/selectors';
import { updateUser } from '../../services/AuthService';

import gb from '../../assets/flags/gb.svg';
import fr from '../../assets/flags/fr.svg';
import es from '../../assets/flags/es.svg';
import de from '../../assets/flags/de.svg';
import ru from '../../assets/flags/ru.svg';
import pt from '../../assets/flags/pt.svg';
import sa from '../../assets/flags/sa.svg';
import cn from '../../assets/flags/cn.svg';
import kr from '../../assets/flags/kr.svg';
import jp from '../../assets/flags/jp.svg';
import th from '../../assets/flags/th.svg';
import vn from '../../assets/flags/vn.svg';
import trFlag from '../../assets/flags/tr.svg';
import inFlag from '../../assets/flags/in.svg';
import idFlag from '../../assets/flags/id.svg';
import itFlag from '../../assets/flags/it.svg';
import plFlag from '../../assets/flags/pl.svg';
import nlFlag from '../../assets/flags/nl.svg';

// code i18n → { drapeau, nom natif, code court }. Ordre = EN en premier, puis alphabétique par code court.
export const LANGUAGES = [
  { code: 'en', flag: gb,     name: 'English',      short: 'EN' },
  { code: 'ar', flag: sa,     name: 'العربية',      short: 'AR' },
  { code: 'de', flag: de,     name: 'Deutsch',      short: 'DE' },
  { code: 'es', flag: es,     name: 'Español',      short: 'ES' },
  { code: 'fr', flag: fr,     name: 'Français',     short: 'FR' },
  { code: 'hi', flag: inFlag, name: 'हिन्दी',       short: 'HI' },
  { code: 'id', flag: idFlag, name: 'Bahasa',       short: 'ID' },
  { code: 'it', flag: itFlag, name: 'Italiano',     short: 'IT' },
  { code: 'ja', flag: jp,     name: '日本語',        short: 'JA' },
  { code: 'ko', flag: kr,     name: '한국어',        short: 'KO' },
  { code: 'nl', flag: nlFlag, name: 'Nederlands',   short: 'NL' },
  { code: 'pl', flag: plFlag, name: 'Polski',       short: 'PL' },
  { code: 'pt', flag: pt,     name: 'Português',    short: 'PT' },
  { code: 'ru', flag: ru,     name: 'Русский',      short: 'RU' },
  { code: 'th', flag: th,     name: 'ภาษาไทย',     short: 'TH' },
  { code: 'tr', flag: trFlag, name: 'Türkçe',       short: 'TR' },
  { code: 'vi', flag: vn,     name: 'Tiếng Việt',   short: 'VI' },
  { code: 'zh', flag: cn,     name: '中文',          short: 'ZH' },
];

const Root = styled.div`
  position: relative;
  display: inline-flex;
`;

/* Trigger = cercle normalisé 2.4rem, même DA que .h-icon-btn header (§header-redesign). */
const Trigger = styled.button`
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 50%;
  border: 1px solid ${({ $open }) => ($open ? 'rgba(194,24,166,.35)' : 'rgba(13,10,16,.1)')};
  background: ${({ $open }) => ($open ? 'rgba(194,24,166,.06)' : 'transparent')};
  display: grid;
  place-items: center;
  cursor: pointer;
  padding: 0;
  transition: background 0.15s ease, border-color 0.15s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(194,24,166,.06);
    border-color: rgba(194,24,166,.28);
  }
  &:focus-visible { outline: none; }
`;

/* Drapeau circulaire : crop SVG rectangulaire → cercle via overflow:hidden */
const FlagCircle = styled.div`
  width: 1.55rem;
  height: 1.55rem;
  border-radius: 50%;
  overflow: hidden;
  display: grid;
  place-items: center;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 0.6rem);
  right: 0;
  background: ${COLORS.white};
  border: 1px solid ${COLORS.line};
  border-top: 0.2rem solid ${COLORS.accent};
  border-radius: ${RADII.card};
  box-shadow: ${SHADOW.dropdown};
  padding: 0.75rem 0.8rem 0.8rem;
  z-index: 1000;
  min-width: 13rem;

  &::before {
    content: "";
    position: absolute;
    top: -0.52rem;
    right: 0.85rem;
    width: 0.85rem;
    height: 0.85rem;
    background: ${COLORS.white};
    border-left: 1px solid ${COLORS.line};
    border-top: 1px solid ${COLORS.line};
    transform: rotate(45deg);
  }

  [dir="rtl"] & { right: auto; left: 0; }
  [dir="rtl"] &::before { right: auto; left: 0.85rem; }
`;

const MenuTitle = styled.div`
  font-family: 'Roboto', Helvetica, Arial, sans-serif;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(13,10,16,.4);
  margin-bottom: 0.6rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.4rem;
`;

const Item = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.22rem;
  padding: 0.3rem;
  background: ${({ $active }) => ($active ? 'rgba(194,24,166,.1)' : 'transparent')};
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background 0.12s ease;
  outline: ${({ $active }) => ($active ? '1.5px solid rgba(194,24,166,.4)' : 'none')};

  &:hover { background: rgba(194,24,166,.07); }
`;

const ItemFlag = styled.div`
  width: 2.1rem;
  height: 2.1rem;
  border-radius: 50%;
  overflow: hidden;
  border: 1.5px solid ${({ $active }) => ($active ? COLORS.accent : 'rgba(13,10,16,.08)')};
  transition: border-color 0.12s ease;
  display: grid;
  place-items: center;
  background: rgba(244,241,246,.6);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const ItemCode = styled.span`
  font-family: 'Roboto', Helvetica, Arial, sans-serif;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: ${({ $active }) => ($active ? COLORS.accent : 'rgba(13,10,16,.45)')};
`;

export default function LanguageSelect() {
  const { i18n } = useTranslation();
  const signedUser = useSelector(selectors.user);
  const [open, setOpen] = useState(false);
  const ref = useOnclickOutside(() => setOpen(false));

  const current = LANGUAGES.find(l => l.code === i18n.language)
    || LANGUAGES.find(l => i18n.language && i18n.language.startsWith(l.code))
    || LANGUAGES[0];

  const select = (code) => {
    setOpen(false);
    if (code === i18n.language) return;
    i18n.changeLanguage(code); // persiste en localStorage (LanguageDetector)
    if (signedUser && signedUser.id) {
      // synchro backend non-bloquante (la langue marche déjà via localStorage)
      updateUser(signedUser.id, { preferredLanguage: code })
        .catch(e => console.error('[LanguageSelect] preferredLanguage sync:', e && e.message));
    }
  };

  return (
    <Root ref={ref}>
      <Trigger
        type="button"
        aria-label={`Language: ${current.name}`}
        title={current.name}
        $open={open}
        onClick={() => setOpen(o => !o)}
      >
        <FlagCircle>
          <img src={current.flag} loading="lazy" alt={current.name} />
        </FlagCircle>
      </Trigger>
      {open && (
        <Menu role="listbox" aria-label="Select language">
          <MenuTitle>Language</MenuTitle>
          <Grid>
            {LANGUAGES.map(l => (
              <Item
                key={l.code}
                type="button"
                role="option"
                aria-selected={l.code === current.code}
                aria-label={l.name}
                title={l.name}
                $active={l.code === current.code}
                onClick={() => select(l.code)}
              >
                <ItemFlag $active={l.code === current.code}>
                  <img src={l.flag} loading="lazy" alt="" />
                </ItemFlag>
                <ItemCode $active={l.code === current.code}>{l.short}</ItemCode>
              </Item>
            ))}
          </Grid>
        </Menu>
      )}
    </Root>
  );
}
